<template>
  <div class="global-session">
    <div class="global-session__body">
      <div v-if="isInitialStatus && !isOtherSessionRunning">
        <p class="global-session__subtitle">
          <i class="material-icons global-session__subtitle-icon"
            >info_outline</i
          >
          Lab not running
        </p>
        <p class="global-session__text">
          Start the lab and you’ll be able to download a VPN File and connect.
        </p>
      </div>
      <div v-else-if="isRunningStatus">
        <p class="global-session__subtitle">
          <i class="material-icons global-session__subtitle-icon"
            >info_outline</i
          >
          Lab running
        </p>
        <p class="global-session__text">
          Download the VPN file and use the provided credentials to login.
        </p>
        <p class="global-session__text">
          Please
          <span class="global-session__text--highlight"
            >keep this window open</span
          >
          while you’re working on the lab. Labs that don't register activity
          <span class="global-session__text--highlight">
            will be automatically stopped.
          </span>
        </p>
        <button
          class="global-session__flat-btn"
          @click.prevent="downloadVpnFile()"
          :disabled="isLoadingDownload"
        >
          <KeyIcon class="global-session__flat-vpn-file-icon" />
          <span class="global-session__flat-btn-text">
            Download VPN file
          </span>
          <ActionLoader v-if="isLoadingDownload" name="ball-clip-rotate" />
        </button>
        <div class="global-session__credential-box">
          <div class="global-session__credential-icons-box">
            <UserIcon class="global-session__credential-icon" />
          </div>
          <input
            class="global-session__credential-input"
            v-model="currentSession.vpn_username"
            :readonly="true"
          />
          <div class="global-session__credential-icons-box">
            <CopyIcon
              class="global-session__credential-icon global-session__credential-icon--pointer"
              @click="copyToClipboard('username')"
              v-tooltip="{
                content: 'Copy to clipboard',
                placement: 'top-center',
              }"
            />
          </div>
        </div>
        <div class="global-session__credential-box">
          <div class="global-session__credential-icons-box">
            <LockIcon class="global-session__credential-icon" />
          </div>
          <input
            class="global-session__credential-input"
            v-model="currentSession.vpn_password"
            :type="isVpnPasswordVisible ? 'text' : 'password'"
            :readonly="true"
          />
          <div class="global-session__credential-icons-box">
            <EyeIcon
              class="global-session__credential-icon global-session__credential-icon--pointer"
              @click="toggleVpnPassword"
              v-tooltip="{
                content: `${
                  isVpnPasswordVisible ? 'Hide' : 'Show'
                } VPN password`,
                placement: 'top-center',
              }"
            />
            <CopyIcon
              class="global-session__credential-icon global-session__credential-icon--pointer"
              @click="copyToClipboard('password')"
              v-tooltip="{
                content: 'Copy to clipboard',
                placement: 'top-center',
              }"
            />
          </div>
        </div>
      </div>
      <div v-else-if="isStoppedStatus && !isOtherSessionRunning">
        <div v-if="!isResetConfirmationOpen">
          <p class="global-session__subtitle">
            <i class="material-icons global-session__subtitle-icon"
              >info_outline</i
            >
            Lab stopped
          </p>
          <p v-if="isAllowedToResetSession" class="global-session__text">
            Your lab environment is stopped and its state has been preserved.
            Use the button below to reset it back to the original.
          </p>
          <p v-else class="global-session__text">
            You’ve exhausted the number of resets available per day.
          </p>
          <button
            class="global-session__flat-btn"
            @click="toggleResetConfirmation()"
            :disabled="!isAllowedToResetSession"
          >
            <ResetIcon class="global-session__reset-icon" />
            <span class="global-session__flat-btn-text">
              Reset Lab
            </span>
          </button>
        </div>
        <div v-if="isResetConfirmationOpen">
          <p class="global-session__subtitle">
            <i class="material-icons global-session__subtitle-icon"
              >info_outline</i
            >
            Are you sure you want to reset your lab?
          </p>
          <p class="global-session__text">
            This can't be undone. You can reset it MAX 4 times per day.
            <span class="global-session__text--highlight">
              {{ availableResets }} remaining.
            </span>
          </p>
          <div class="global-session__confirmation-box">
            <button
              class="global-session__btn global-session__btn--filled"
              @click="toggleResetConfirmation()"
            >
              Cancel
            </button>
            <button
              class="global-session__btn global-session__btn--filled global-session__btn--red"
              @click="resetSession()"
            >
              Yes, reset my lab
            </button>
          </div>
        </div>
      </div>
      <div v-else>
        <p class="global-session__subtitle">
          <i class="material-icons global-session__subtitle-icon"
            >info_outline</i
          >
          You can't start a lab
        </p>
        <p class="global-session__text">
          There's another Lab currently running
        </p>
        <p class="global-session__dark-label">
          <ELSLabIcon class="dark-label-icon" />
          {{ globalRunningLabName }}
        </p>
        <button class="global-session__flat-btn" @click="stopGlobalSession()">
          <ResetIcon class="global-session__reset-icon" />
          <span class="global-session__flat-btn-text">
            Stop this lab
          </span>
          <ActionLoader v-if="isLoadingGlobalSession" name="ball-clip-rotate" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ActionLoader from '@/components/shared/ActionLoader.vue'
import ELSLabIcon from '@/assets/icons/course/lab/els-lab-icon.svg'
import StatusChangingIcon from '@/assets/icons/course/lab/status-changing-icon.svg'
import CheckIcon from '@/assets/icons/course/lab/check-icon.svg'
import ResetIcon from '@/assets/icons/course/lab/reset-icon.svg'
import KeyIcon from '@/assets/icons/course/lab/key-icon.svg'
import UserIcon from '@/assets/icons/course/lab/user-icon.svg'
import LockIcon from '@/assets/icons/course/lab/lock-icon.svg'
import CopyIcon from '@/assets/icons/course/lab/copy-icon.svg'
import EyeIcon from '@/assets/icons/course/lab/eye-icon.svg'

export default {
  components: {
    ActionLoader,
    ELSLabIcon: { ...ELSLabIcon, name: 'ELSLabIcon' },
    ResetIcon: { ...ResetIcon, name: 'ResetIcon' },
    KeyIcon: { ...KeyIcon, name: 'KeyIcon' },
    UserIcon: { ...UserIcon, name: 'UserIcon' },
    LockIcon: { ...LockIcon, name: 'LockIcon' },
    CopyIcon: { ...CopyIcon, name: 'CopyIcon' },
    EyeIcon: { ...EyeIcon, name: 'EyeIcon' },
  },
  props: {
    currentLab: {
      type: Object,
      required: true,
    },
    currentSession: {
      type: Object,
      required: false,
    },
  },
  data: () => {
    return {
      isLoadingGlobalSession: false,
      isLoadingDownload: false,
      isVpnPasswordVisible: false,
      isResetConfirmationOpen: false,
    }
  },
  computed: {
    status() {
      const sessionStatus =
        this.currentSession && this.currentSession.status
          ? this.currentSession.status
          : null
      return sessionStatus
    },
    isInitialStatus() {
      return ['starting', 'terminating', 'terminated', 'error', null].includes(
        this.status
      )
    },
    isRunningStatus() {
      return ['started', 'stopping'].includes(this.status)
    },
    isStoppedStatus() {
      return ['stopped'].includes(this.status)
    },
    globalRunningSession() {
      return this.currentLab.global_running_session
    },
    isOtherSessionRunning() {
      const currentSessionId = this.currentSession
        ? this.currentSession.id
        : null
      return (
        this.globalRunningSession &&
        this.globalRunningSession.id !== currentSessionId
      )
    },
    globalRunningLabName() {
      return this.globalRunningSession && this.globalRunningSession.lab.name
    },
    availableResets() {
      return this.currentLab.available_resets
    },
    isAllowedToResetSession() {
      return this.availableResets > 0
    },
  },
  methods: {
    async resetSession() {
      this.isLoadingGlobalSession = true
      await this.$store.dispatch('labs/partialUpdateLabSession', {
        labId: this.currentLab.id,
        sessionId: this.currentSession.id,
        status: 'terminated',
      })
      await this.$store.dispatch('labs/getLabById', this.currentLab.id)
      this.toggleResetConfirmation()
      this.isLoadingGlobalSession = false
    },
    async stopGlobalSession() {
      this.isLoadingGlobalSession = true
      await this.$store.dispatch('labs/partialUpdateLabSession', {
        labId: this.globalRunningSession.lab.id,
        sessionId: this.globalRunningSession.id,
        status: 'stopped',
        cleanGlobalSession: true,
      })
      this.isLoadingGlobalSession = false
    },
    async downloadVpnFile() {
      this.isLoadingDownload = true
      const response = await this.$store.dispatch('labs/getLabSessionVPNFile', {
        labId: this.currentLab.id,
        sessionId: this.currentSession.id,
      })
      if (response) {
        const anchor = document.createElement('a')
        anchor.href = window.URL.createObjectURL(
          new Blob([response], { type: 'text/plain' })
        )
        anchor.download = `${this.currentLab.slug}.ovpn`
        anchor.click()
      }
      this.isLoadingDownload = false
    },
    async copyToClipboard(type) {
      if (!type) return
      try {
        await navigator.clipboard.writeText(this.currentSession[`vpn_${type}`])
        this.$store.dispatch(
          'app/setSuccess',
          `VPN ${type} has been copied to your clipboard.`
        )
      } catch (err) {
        this.$store.dispatch(
          'app/setError',
          "Your browser doesn't allow clipboard access."
        )
      }
    },
    toggleVpnPassword() {
      this.isVpnPasswordVisible = !this.isVpnPasswordVisible
    },
    toggleResetConfirmation() {
      this.isResetConfirmationOpen = !this.isResetConfirmationOpen
    },
  },
}
</script>

<style lang="scss" scoped>
.global-session {
  max-width: 100%;
  width: 100%;
  background-color: var(--flat-black);
  display: flex;
  flex-direction: column;
  align-self: stretch;
  align-items: flex-start;
  justify-content: center;
  padding: 30px;
  @include tablet {
    max-width: 50%;
  }
  &__body {
    text-align: left;
  }
  &__subtitle {
    font-weight: 700;
    text-transform: uppercase;
    display: flex;
    align-items: flex-start;
    margin-bottom: 10px;
    &-icon {
      color: var(--lighter-gray);
      font-size: 26px;
      margin: -3px 5px 0 0;
    }
  }
  &__text {
    &--highlight {
      color: var(--orange);
    }
  }
  &__dark-label {
    background-color: var(--dark-gray);
    font-size: 14px;
    font-weight: 700;
    display: flex;
    justify-content: center;
    border-radius: 15px;
    padding: 5px 15px;
    align-items: center;
    .dark-label-icon {
      min-width: 16px;
      fill: white;
      vertical-align: middle;
      margin-right: 7px;
      max-height: 22px;
    }
  }
  &__credential-box {
    border: 1px solid #77777744;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    margin-top: 12px;
  }
  &__credential-input {
    width: 100%;
    color: var(--white);
    background-color: transparent;
    border: none;
    flex-grow: 1;
    overflow-y: auto;
    padding: 0px;
    margin: 0px;
  }
  &__credential-icons-box {
    display: flex;
    white-space: nowrap;
    margin: 4px;
  }
  &__credential-icon {
    fill: transparent;
    stroke: var(--lighter-gray);
    stroke-width: 2px;
    margin: 0 6px;
    &--pointer {
      cursor: pointer;
    }
  }
  &__btn {
    border-radius: 15px;
    padding: 5px 10px;
    font-size: 11px;
    background-color: transparent;
    color: white;
    border: 1px solid var(--orange);
    margin-top: 10px;
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    text-transform: uppercase;
    cursor: pointer;
    &:disabled {
      background-color: transparent;
      color: var(--flat-black);
      border: 1px solid var(--flat-black);
      cursor: not-allowed;
    }
    &--filled {
      background-color: var(--lighter-gray);
      font-weight: 700;
      border: none;
      margin: 0px 10px 0px 0px;
    }
    &--red {
      background-color: var(--lab-status-red);
    }
  }
  &__confirmation-box {
    display: flex;
  }
  &__flat-btn {
    background: transparent;
    color: var(--white);
    border: none;
    display: flex;
    align-items: center;
    padding: 0px;
    cursor: pointer;
    &:disabled {
      cursor: not-allowed;
      .global-session__flat-btn-text {
        color: var(--lighter-gray);
        border-color: var(--lighter-gray);
      }
      .global-session__reset-icon {
        stroke: var(--lighter-gray);
      }
    }
    &-text {
      font-size: 12px;
      font-weight: 700;
      border-bottom: 1px solid var(--white);
      margin: 0px 5px;
    }
  }
  &__reset-icon {
    fill: transparent;
    stroke-width: 2px;
    stroke: var(--orange);
  }
}
</style>
